import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { message } from 'antd';
import axios from 'axios';
import {
    PlusOutlined,
    SearchOutlined,
    ToolOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { connectSocket, releaseSocket } from '@/services/socketClient';
import useMediaQuery from '@/hooks/useMediaQuery/useMediaQuery';

import ProjectSidebar from './ProjectSidebar';
import DashboardFilters from './DashboardFilters';
import PipelineFunnelCards from './PipelineFunnelCards';
import WorkloadChart from './WorkloadChart';
import AIIntelligenceCenter from './AIIntelligenceCenter';
import TaskViewTabs from './TaskViewTabs';
import TaskListTable from './TaskListTable';
import TaskDetailDrawer from './TaskDrawer';
import KanbanBoard from './kanban';
import TimelineBoard from './timeline';
import SprintBoard from './sprint';
import MilestoneCardView from './MilestoneCardView';
import ProjectDashboard from '../ProjectDashboard';
import ProjectDetailsWorkspace from './projectDetails';
import ProjectFiles from './files';
import ProjectInbox from './inbox';
import { getInboxFeed } from './inbox/api/inboxApi';
import MyWork from './mywork';
import ReportsWorkspace from './reports';
// ── Category 3: Omni/CRE Integration Status Banner (easily removable/revertible) ──
import CreSyncWarningBanner from './CreSyncWarningBanner';

import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { usePermission } from '@/customHooks/usePermission';

import {
    fetchTasks,
    fetchTaskSummary,
    fetchProjects,
    deleteTask,
    updateTaskStatus,
    convertWorkItemType,
    fetchTaskById,
} from './api/projectManagementApi';

import MilestoneCompletionModal from './TaskDrawer/components/MilestoneCompletionModal';
import { ProjectManagementTourProvider, ProjectManagementTour, TakeTourButton, useProjectTour } from './tour';

const CreateProjectModal = React.lazy(() => import('./modals/CreateProjectModal'));

/**
 * MilestoneTourSynchronizer
 * Listens to active tour state. When in the milestones tour and the step reaches
 * the milestone drawer breakdown, automatically opens the first milestone into
 * TaskDetailDrawer so all drawer components mount and are highlighted.
 * Automatically closes the drawer if it was opened by the tour when finished.
 */
function MilestoneTourSynchronizer({ tasks = [], openTaskDrawer, isDrawerOpen, closeTaskDrawer }) {
    const { isOpen, activeTourId, currentStep, currentTourDefinition } = useProjectTour();
    const openedByTourRef = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            if (openedByTourRef.current) {
                openedByTourRef.current = false;
                closeTaskDrawer?.();
            }
            return;
        }

        if (activeTourId === 'milestones' && currentTourDefinition?.steps) {
            const step = currentTourDefinition.steps[currentStep];
            const isDrawerStep = Boolean(
                step?.isDrawerStep ||
                (step?.selector && (
                    step.selector.includes('milestone-progress-card') ||
                    step.selector.includes('milestone-completion-criteria') ||
                    step.selector.includes('milestone-deliverables-checklist') ||
                    step.selector.includes('task-subtasks-section')
                ))
            );

            if (isDrawerStep && !isDrawerOpen) {
                const firstMs = tasks.find((t) => t.itemType === 'milestone') || tasks[0];
                if (firstMs && openTaskDrawer) {
                    openedByTourRef.current = true;
                    openTaskDrawer(firstMs._id);
                }
            }
        }
    }, [isOpen, activeTourId, currentStep, currentTourDefinition, tasks, isDrawerOpen, openTaskDrawer, closeTaskDrawer]);

    return null;
}

/**
 * ProjectManagementDashboard
 * Sidebar-driven navigation: Overview (dashboard), Milestones, plus future-phase stubs.
 */
export default function ProjectManagementDashboard({ initialNav }) {
    // ── Data state ──────────────────────────────────────────────────────────
    const [tasks, setTasks] = useState([]);
    const [taskSummary, setTaskSummary] = useState({});
    const [projects, setProjects] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [activeProject, setActiveProject] = useState(null);
    const [loading, setLoading] = useState(false);

    const currentAdmin = useSelector(selectCurrentAdmin);
    const userRole = (currentAdmin?.role || currentAdmin?.adminRole || '').toLowerCase();
    const isEmployee = userRole === 'employee';
    const {
        canCreate: canCreatePM,
        canDelete: canDeletePM,
        canApprove: canApprovePM,
        canExport: canExportPM,
    } = usePermission('ProjectManagement');
    const {
        canApprove: canApproveTasks,
        canExport: canExportTasks,
    } = usePermission('Tasks');
    const isElevated = ['super_admin', 'admin', 'project manager', 'manager'].includes(userRole);
    const canViewAllTasks = isElevated;
    // const canCreateMilestone = isElevated || canCreatePM;
    const canCreateMilestone = true; // Restriction commented out for now — anyone can create milestones
    const hasTaskCreateAccess = true; // All authenticated users are allowed to create tasks

    const location = useLocation();
    const history = useHistory();
    // Sidebar breakpoints:
    //   < 768px    → fully hidden (isHidden=true, no rail, no toggle)
    //   768–1199px → collapsed to icon rail (isOpen=false)
    //   ≥ 1200px   → fully expanded (isOpen=true)
    //   files tab ≤ 1300px → auto-collapsed to icon rail to maximize files workspace
    const isMobileHidden = useMediaQuery('(max-width: 767px)');
    const isMobile = useMediaQuery('(max-width: 1199px)');
    const isFilesNarrow = useMediaQuery('(max-width: 1300px)');

    // ── Sidebar open/close ────────────────────────────────────────────────────
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        if (typeof window === 'undefined') return true;
        const initialActiveNav = initialNav || (location.pathname.includes('project-dashboard') ? 'dashboard' : 'overview');
        if (initialActiveNav === 'files') {
            return window.innerWidth > 1300;
        }
        return window.innerWidth >= 1200;
    });

    // ── Active Section ───────────────────────────────────────────────────────
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const queryProjectId = queryParams.get('projectId');
    const queryProjectTab = queryParams.get('tab') || 'overview';

    const hasDefaultedNav = useRef(false);
    const [activeNav, setActiveNav] = useState(() => {
        if (initialNav) return initialNav;
        if (new URLSearchParams(location.search).get('projectId')) return 'dashboard';
        if (location.state?.activeNav) return location.state.activeNav;
        if (location.pathname.includes('project-dashboard')) return 'dashboard';
        if (isEmployee) return 'mywork';
        return 'overview';
    }); // sidebar nav key

    // Ensure employees land on 'mywork' if user auth state resolves after initial mount
    useEffect(() => {
        if (!hasDefaultedNav.current && isEmployee && activeNav === 'overview' && !location.state?.activeNav && !initialNav) {
            hasDefaultedNav.current = true;
            setActiveNav('mywork');
        }
    }, [isEmployee, activeNav, location.state, initialNav]);

    const [inboxBadgeCount, setInboxBadgeCount] = useState(0);

    // Synchronize live inbox badge count dynamically
    useEffect(() => {
        let isMounted = true;
        const loadInboxBadge = async () => {
            try {
                const res = await getInboxFeed({ tab: 'all', limit: 1 });
                if (isMounted && res?.data?.counts) {
                    const count = res.data.counts.action || res.data.counts.unread || 0;
                    setInboxBadgeCount(count);
                }
            } catch (err) {
                // graceful fallback
            }
        };
        loadInboxBadge();
        return () => { isMounted = false; };
    }, [activeNav]);

    // Live badge count of active tasks directly assigned to the current employee (excludes collaborators/reviewers and drafts)
    const myWorkBadgeCount = useMemo(() => {
        if (!currentAdmin?._id || !Array.isArray(tasks)) return 0;
        return tasks.filter((t) => {
            if (t.isDraft || t.status === 'Draft') return false;
            const isAssignee = String(t.assignee?._id || t.assignee) === String(currentAdmin._id);
            const isActive = t.status !== 'Completed' && t.status !== 'Closed' && t.status !== 'Cancelled';
            return isAssignee && isActive;
        }).length;
    }, [tasks, currentAdmin]);

    useEffect(() => {
        const p = new URLSearchParams(location.search);
        if (p.get('projectId')) {
            setActiveNav('dashboard');
            return;
        }
        if (location.pathname.includes('project-dashboard')) {
            setActiveNav('dashboard');
        } else if (location.pathname.includes('project-management') && activeNav === 'dashboard') {
            setActiveNav(isEmployee ? 'mywork' : 'overview');
        }
    }, [location.pathname, location.search, isEmployee]);

    // Auto-collapse into icon rail on < 1200px, expand on >= 1200px.
    // For Files tab specifically: auto-collapse on ≤ 1300px so dual sidebars don't squeeze the files workspace.
    useEffect(() => {
        if (activeNav === 'files') {
            setSidebarOpen(!isFilesNarrow);
        } else {
            setSidebarOpen(!isMobile);
        }
    }, [activeNav, isFilesNarrow, isMobile]);

    // Lock outer window scrolling only while strictly active inside Files tab; cleanly restore for all other views
    useEffect(() => {
        if (activeNav === 'files') {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [activeNav]);

    const handleSidebarToggle = () => {
        setSidebarOpen((prev) => !prev);
    };

    // ── UI state ─────────────────────────────────────────────────────────────
    const [activeTaskTab, setActiveTaskTab] = useState('list');  // list/kanban/timeline/sprint
    const [visitedTabs, setVisitedTabs] = useState(() => new Set(['list']));
    const [visitedNavs, setVisitedNavs] = useState(() => new Set([activeNav, new URLSearchParams(location.search).get('projectId') ? 'dashboard' : null].filter(Boolean)));

    useEffect(() => {
        setVisitedNavs((prev) => {
            if (prev.has(activeNav)) return prev;
            const next = new Set(prev);
            next.add(activeNav);
            return next;
        });
    }, [activeNav]);

    const [statusFilter, setStatusFilter] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
    const searchContainerRef = useRef(null);

    const [dashboardTargetProjectId, setDashboardTargetProjectId] = useState(null);
    const [listPage, setListPage] = useState(1);
    const TABLE_PAGE_SIZE = 10;
    const [tableTasks, setTableTasks] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [tablePagination, setTablePagination] = useState({
        page: 1,
        limit: TABLE_PAGE_SIZE,
        total: 0,
        totalPages: 1,
    });

    const [milestones, setMilestones] = useState([]);
    const [milestonesLoading, setMilestonesLoading] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Click outside handler for search dropdown
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
                setSearchDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        setVisitedTabs((prev) => {
            if (prev.has(activeTaskTab)) return prev;
            const next = new Set(prev);
            next.add(activeTaskTab);
            return next;
        });
    }, [activeTaskTab]);

    const [filters, setFilters] = useState({
        dateRange: 'All Time',
        owner: '',
        assignee: '',
        projectId: '',
        status: '',
    });

    useEffect(() => {
        setListPage(1);
    }, [filters, statusFilter]);

    // ── Drawer / modal state ─────────────────────────────────────────────────
    const [drawerMode, setDrawerMode] = useState('view');
    const [drawerTaskId, setDrawerTaskId] = useState(null);
    const [drawerDefaultStatus, setDrawerDefaultStatus] = useState('Backlog');
    const [drawerSelectedProject, setDrawerSelectedProject] = useState(null);
    const [drawerInitialMilestoneId, setDrawerInitialMilestoneId] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
    const [projectToEdit, setProjectToEdit] = useState(null);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    // ── Milestone Completion Modal state ─────────────────────────────────────
    const [completingMilestone, setCompletingMilestone] = useState(null);
    const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
    const [completionLoading, setCompletionLoading] = useState(false);

    // ── Deep-link from notification click or URL query param ────────────────
    const [pendingAlertId, setPendingAlertId] = useState(null);
    const handledDeepLinkTaskIdRef = useRef(null);

    useEffect(() => {
        // Deep-link from query param ?task=TSK-... or ?taskId=...
        const searchParams = new URLSearchParams(location.search);
        const queryTaskId = searchParams.get('task') || searchParams.get('taskId');
        if (queryTaskId && handledDeepLinkTaskIdRef.current !== queryTaskId) {
            handledDeepLinkTaskIdRef.current = queryTaskId;
            hasDefaultedNav.current = true;
            setActiveNav(location.state?.activeNav || (isEmployee ? 'mywork' : 'overview'));
            openTaskDrawer(queryTaskId);

            // Strip the query param from URL bar without cancelling the drawer open
            searchParams.delete('task');
            searchParams.delete('taskId');
            const newSearch = searchParams.toString();
            history.replace({
                pathname: location.pathname,
                search: newSearch ? `?${newSearch}` : '',
                state: undefined,
            });
            return;
        }

        const state = location.state;
        if (!state) return;

        if (state.taskId && handledDeepLinkTaskIdRef.current !== state.taskId) {
            handledDeepLinkTaskIdRef.current = state.taskId;
            hasDefaultedNav.current = true;
            setActiveNav(state.activeNav || (isEmployee ? 'mywork' : 'overview'));
            openTaskDrawer(state.taskId);
            history.replace(location.pathname);
            return;
        }

        if (state.alertId) {
            setActiveNav('overview');
            setPendingAlertId(state.alertId);
        }

        if (state.openProjectModal) {
            setVisitedNavs((prev) => {
                if (prev.has('dashboard')) return prev;
                const next = new Set(prev);
                next.add('dashboard');
                return next;
            });
            setActiveNav('dashboard');
            if (state.projectId) {
                setDashboardTargetProjectId(state.projectId);
            }
            history.replace(location.pathname);
            return;
        }

        if (state.projectId) {
            setActiveNav('overview');
            setFilters(prev => ({ ...prev, projectId: state.projectId }));
        }

        if (state.activeNav) {
            setActiveNav(state.activeNav);
        }

        history.replace(location.pathname);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, location.state]);

    // ── Data fetching ────────────────────────────────────────────────────────
    const loadEmployees = useCallback(async () => {
        try {
            const { data } = await axios.get('/employeesNames');
            const list = (data?.names || []).map((e) => ({
                _id: e.id,
                name: e.fullName || `${e.name || ''} ${e.surname || ''}`.trim(),
            }));
            setEmployees(list);
        } catch { /* non-critical */ }
    }, []);

    const loadProjects = useCallback(async () => {
        try {
            const { data } = await fetchProjects();
            const list = data?.result || data?.projects || [];
            setProjects(list);
        } catch { /* non-critical */ }
    }, []);

    const summaryDebounceTimerRef = useRef(null);
    const loadTaskSummary = useCallback(async () => {
        try {
            const params = {};
            if (filters.projectId) params.projectId = filters.projectId;
            if (filters.owner) params.owner = filters.owner;
            if (filters.assignee) params.assignee = filters.assignee;
            const { data } = await fetchTaskSummary(params);
            setTaskSummary(data?.result || data || {});
        } catch { setTaskSummary({}); }
    }, [filters.projectId, filters.owner, filters.assignee]);

    // Debounced wrapper: if 50 socket events hit in 1-2 seconds, fire only ONE HTTP summary request
    const debouncedLoadTaskSummary = useCallback(() => {
        if (summaryDebounceTimerRef.current) {
            clearTimeout(summaryDebounceTimerRef.current);
        }
        summaryDebounceTimerRef.current = setTimeout(() => {
            loadTaskSummary();
        }, 1500);
    }, [loadTaskSummary]);

    useEffect(() => {
        return () => {
            if (summaryDebounceTimerRef.current) {
                clearTimeout(summaryDebounceTimerRef.current);
            }
        };
    }, []);

    const loadTableTasks = useCallback(async (page = 1, limit = TABLE_PAGE_SIZE) => {
        setTableLoading(true);
        try {
            const params = {
                page,
                limit,
                itemType: 'task',
                excludeMilestones: true,
            };
            if (filters.projectId) params.projectId = filters.projectId;
            if (filters.owner) params.owner = filters.owner;
            if (filters.assignee) params.assignee = filters.assignee;
            if (filters.dateRange && filters.dateRange !== 'All Time') params.dateRange = filters.dateRange;
            if (statusFilter) params.status = statusFilter;

            const { data } = await fetchTasks(params);
            setTableTasks(data?.result || []);
            if (data?.pagination) {
                setTablePagination({
                    page: data.pagination.page || page,
                    limit: data.pagination.limit || limit,
                    total: data.pagination.total || 0,
                    totalPages: data.pagination.totalPages || 1,
                });
            }
        } catch {
            message.error('Failed to load table tasks.');
            setTableTasks([]);
        } finally {
            setTableLoading(false);
        }
    }, [filters.projectId, filters.owner, filters.assignee, filters.dateRange, statusFilter]);

    // Dedicated search dropdown query execution (does NOT touch table or reset pages)
    useEffect(() => {
        const q = debouncedSearchQuery.trim();
        if (!q) {
            setSearchResults([]);
            setSearchLoading(false);
            setSearchDropdownOpen(false);
            return;
        }

        let isMounted = true;
        setSearchLoading(true);
        setSearchDropdownOpen(true);

        const params = { search: q, limit: 8 };
        if (filters.projectId) params.projectId = filters.projectId;

        fetchTasks(params)
            .then(({ data }) => {
                if (isMounted) {
                    setSearchResults(data?.result || []);
                }
            })
            .catch(() => {
                if (isMounted) setSearchResults([]);
            })
            .finally(() => {
                if (isMounted) setSearchLoading(false);
            });

        return () => { isMounted = false; };
    }, [debouncedSearchQuery, filters.projectId]);

    const loadTasks = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 'all' };
            if (filters.projectId) params.projectId = filters.projectId;
            if (filters.owner) params.owner = filters.owner;
            if (filters.assignee) params.assignee = filters.assignee;
            if (filters.dateRange && filters.dateRange !== 'All Time') params.dateRange = filters.dateRange;
            if (filters.itemType) params.itemType = filters.itemType;
            if (statusFilter) params.status = statusFilter;
            const { data } = await fetchTasks(params);
            setTasks(data?.result || []);
        } catch {
            message.error('Failed to load tasks.');
            setTasks([]);
        } finally {
            setLoading(false);
        }
    }, [filters.projectId, filters.owner, filters.assignee, filters.dateRange, filters.itemType, statusFilter]);

    const loadMilestones = useCallback(async () => {
        setMilestonesLoading(true);
        try {
            const params = { itemType: 'milestone' };
            if (filters.projectId) params.projectId = filters.projectId;
            if (filters.owner) params.owner = filters.owner;
            if (filters.assignee) params.assignee = filters.assignee;
            const { data } = await fetchTasks(params);
            setMilestones(data?.result || []);
        } catch {
            message.error('Failed to load milestones.');
            setMilestones([]);
        } finally {
            setMilestonesLoading(false);
        }
    }, [filters.projectId, filters.owner, filters.assignee]);

    // Initial startup fetch (runs ONCE on mount)
    useEffect(() => {
        loadEmployees();
        loadProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const overviewLoadedRef = useRef(false);
    const milestonesLoadedRef = useRef(false);
    const prevFiltersRef = useRef(filters);
    const prevStatusFilterRef = useRef(statusFilter);

    // Re-fetch tasks, milestones, or inbox/files/dashboard based on activeNav and filters
    useEffect(() => {
        const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters) || prevStatusFilterRef.current !== statusFilter;
        if (filtersChanged) {
            prevFiltersRef.current = filters;
            prevStatusFilterRef.current = statusFilter;
            overviewLoadedRef.current = false;
            milestonesLoadedRef.current = false;
        }

        if (activeNav === 'milestones') {
            if (!milestonesLoadedRef.current) {
                milestonesLoadedRef.current = true;
                loadMilestones();
            }
        } else if (activeNav === 'inbox' || activeNav === 'files' || activeNav === 'dashboard') {
            // Self-contained submodules: Inbox, Files, and Dashboard manage their own scoped data
        } else {
            // Overview or My Work
            if (!overviewLoadedRef.current) {
                overviewLoadedRef.current = true;
                loadTableTasks(1, TABLE_PAGE_SIZE);
                loadTasks();
                loadTaskSummary();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, statusFilter, activeNav]);

    // Socket — live effort and task updates
    useEffect(() => {
        if (!currentAdmin?._id) return;
        const socket = connectSocket({ userId: currentAdmin._id, role: currentAdmin.role });
        if (!socket) return;

        const handleEffortUpdate = (payload) => {
            const updatedTaskId = String(payload?.taskId || '');
            if (!updatedTaskId) return;
            setTasks((prev) => prev.map((task) =>
                String(task._id) === updatedTaskId
                    ? {
                        ...task,
                        actualTimeMs: typeof payload.actualTimeMs === 'number'
                            ? payload.actualTimeMs
                            : task.actualTimeMs,
                    }
                    : task
            ));
        };

        const handleProjectTaskUpdated = async (payload) => {
            const taskId = String(payload?.taskId || '');
            const changeType = payload?.changeType;
            if (!taskId) return;

            if (changeType === 'deleted') {
                setTasks((prev) => prev.filter((t) => String(t._id) !== taskId));
                debouncedLoadTaskSummary();
            } else {
                try {
                    const { data } = await fetchTaskById(taskId);
                    const taskObj = data?.result;
                    if (taskObj) {
                        setTasks((prev) => {
                            const exists = prev.some((t) => String(t._id) === taskId);
                            if (exists) {
                                return prev.map((t) => String(t._id) === taskId ? taskObj : t);
                            } else {
                                return [taskObj, ...prev];
                            }
                        });
                        debouncedLoadTaskSummary();
                    }
                } catch (err) {
                    console.error('[Socket] Failed to fetch task details for taskId:', taskId, err);
                }
            }
        };

        socket.on('projectTask:effortUpdated', handleEffortUpdate);
        socket.on('projectTask:updated', handleProjectTaskUpdated);

        return () => {
            socket.off('projectTask:effortUpdated', handleEffortUpdate);
            socket.off('projectTask:updated', handleProjectTaskUpdated);
            releaseSocket();
        };
    }, [currentAdmin?._id, currentAdmin?.role, debouncedLoadTaskSummary]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        if (newFilters.status !== undefined) {
            setStatusFilter(newFilters.status || null);
        }
    };
    const handleStatusFilterChange = (status) => {
        setStatusFilter(status);
        setFilters((prev) => ({ ...prev, status: status || '' }));
    };
    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        setListPage(newPage);
        loadTableTasks(newPage, TABLE_PAGE_SIZE);
    };

    const handleTaskCreated = (newTask) => {
        if (newTask?.itemType === 'milestone') {
            setMilestones((prev) => [newTask, ...prev]);
            loadMilestones();
        } else {
            setTasks((prev) => [newTask, ...prev]);
            loadTableTasks(listPage, TABLE_PAGE_SIZE);
            loadTaskSummary();
        }
    };

    const handleTaskUpdated = (updatedTask) => {
        if (updatedTask?.itemType === 'milestone') {
            setMilestones((prev) => prev.map((m) => (m._id === updatedTask._id ? updatedTask : m)));
            loadMilestones();
        }
        setTasks((prev) => prev.map((t) => (t._id === updatedTask._id ? updatedTask : t)));
        setTableTasks((prev) => prev.map((t) => (t._id === updatedTask._id ? updatedTask : t)));
        loadTaskSummary();
    };

    const handleProjectCreated = (newProject) => {
        setProjects((prev) => [newProject, ...prev]);
        setActiveProject(newProject);
        // Trigger heavy reloads in the background to not block the UI thread and modal closing
        setTimeout(() => {
            loadProjects();
            loadTasks();
            loadTableTasks(1, TABLE_PAGE_SIZE);
            loadTaskSummary();
        }, 50);
    };

    const [drawerItemType, setDrawerItemType] = useState('task');

    const openCreateDrawer = (prefillStatus = 'Backlog', prefillItemType = 'task', explicitTargetProject = null, targetMilestoneId = null) => {
        const targetProj = explicitTargetProject || activeProject;
        const isProjectOwnerOrPM = Boolean(
            targetProj && (
                String(targetProj.projectManager?._id || targetProj.projectManager || '') === String(currentAdmin?._id) ||
                String(targetProj.createdBy?._id || targetProj.createdBy || '') === String(currentAdmin?._id)
            )
        );
        if (prefillItemType === 'milestone' && !canCreateMilestone && !isProjectOwnerOrPM) {
            message.warning("You do not have permission to create milestones.");
            return;
        }
        setDrawerMode('create');
        setDrawerTaskId(null);
        setDrawerDefaultStatus(prefillStatus || 'Backlog');
        setDrawerItemType(prefillItemType || 'task');
        const explicitProject = explicitTargetProject || (
            (filters.projectId && filters.projectId !== 'all')
                ? (projects.find((p) => String(p._id) === String(filters.projectId)) || activeProject || null)
                : null
        );
        setDrawerSelectedProject(explicitProject);
        setDrawerInitialMilestoneId(targetMilestoneId ? String(targetMilestoneId) : null);
        setIsDrawerOpen(true);
    };

    const openTaskDrawer = (taskOrId) => {
        if (!taskOrId) return;
        const rawId = (typeof taskOrId === 'object' && taskOrId !== null)
            ? (taskOrId._id || taskOrId.id || taskOrId.taskId)
            : taskOrId;
        if (!rawId) return;
        let validTaskId = rawId;
        const isHexId = typeof rawId === 'string' && /^[0-9a-fA-F]{24}$/.test(rawId);
        const searchPool = [...tasks, ...milestones];
        if (!isHexId && searchPool.length > 0) {
            const matched = searchPool.find((t) =>
                (t.taskId && rawId && String(t.taskId).toLowerCase() === String(rawId).toLowerCase()) ||
                (t.title && rawId && String(t.title).toLowerCase().includes(String(rawId).toLowerCase())) ||
                (t._id && rawId && String(t._id) === String(rawId))
            );
            if (matched) {
                validTaskId = matched._id;
            }
        }
        setDrawerMode('view');
        setDrawerTaskId(String(validTaskId));
        setDrawerInitialMilestoneId(null);
        setDrawerDefaultStatus('Backlog');
        setDrawerSelectedProject(null);
        setIsDrawerOpen(true);
    };

    const closeTaskDrawer = () => {
        setIsDrawerOpen(false);
        setDrawerMode('view');
        setDrawerTaskId(null);
        setDrawerInitialMilestoneId(null);
        setDrawerDefaultStatus('Backlog');
        setDrawerSelectedProject(null);
        handledDeepLinkTaskIdRef.current = null;
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has('task') || searchParams.has('taskId')) {
            searchParams.delete('task');
            searchParams.delete('taskId');
            const newSearch = searchParams.toString();
            history.replace({
                pathname: location.pathname,
                search: newSearch ? `?${newSearch}` : '',
            });
        }
    };

    // Ref to hold ProjectDetailsWorkspace refresh callback
    const projectDetailsRefreshRef = React.useRef(null);

    const handleTaskCreatedAndOpen = (newTask, opts = {}) => {
        handleTaskCreated(newTask);
        // Refresh ProjectDetailsWorkspace if mounted
        if (typeof projectDetailsRefreshRef.current === 'function') {
            projectDetailsRefreshRef.current();
        }
        if (!opts?.isDraft && !newTask?.isDraft && newTask?.status !== 'Draft') {
            openTaskDrawer(newTask?._id);
        }
    };

    const handleEdit = (task) => openTaskDrawer(task._id);
    const handleRowClick = (task) => openTaskDrawer(task._id);

    const handleDelete = async (taskId) => {
        setDeletingId(taskId);
        try {
            await deleteTask(taskId);
            message.success('Task deleted.');
            setMilestones((prev) => prev.filter((t) => t._id !== taskId));
            setTasks((prev) => prev.filter((t) => t._id !== taskId));
            setTableTasks((prev) => prev.filter((t) => t._id !== taskId));
            if (activeNav === 'milestones') {
                loadMilestones();
            } else {
                loadTableTasks(listPage, TABLE_PAGE_SIZE);
                loadTaskSummary();
            }
        } catch {
            message.error('Failed to delete task.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleConvertType = async (item) => {
        try {
            const { data } = await convertWorkItemType(item._id);
            message.success(data?.message || 'Work item type updated.');
            loadTableTasks(listPage, TABLE_PAGE_SIZE);
            loadTasks();
            loadMilestones();
            loadTaskSummary();
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to convert work item type.');
        }
    };

    const handleNavChange = (navKey, state = {}) => {
        setVisitedNavs((prev) => {
            if (prev.has(navKey)) return prev;
            const next = new Set(prev);
            next.add(navKey);
            return next;
        });
        setActiveNav(navKey);
        // When switching to Files tab on a screen ≤ 1300px, immediately collapse sidebar to icon rail
        if (navKey === 'files' && typeof window !== 'undefined' && window.innerWidth <= 1300) {
            setSidebarOpen(false);
        } else if (navKey !== 'files' && typeof window !== 'undefined' && window.innerWidth >= 1200) {
            // Restore expanded sidebar for other tabs when on standard desktop
            setSidebarOpen(true);
        }
        if (navKey === 'dashboard') {
            history.push('/project-dashboard', state);
        } else {
            history.replace('/project-management', state);
        }
        if (navKey === 'milestones' || navKey === 'overview') {
            setFilters(prev => ({ ...prev, itemType: '' }));
        }
    };

    const handleInboxNavigate = (navKey, options = {}) => {
        handleNavChange(navKey, options);
        if (options?.taskTab) {
            setActiveTaskTab(options.taskTab);
        }
    };

    const handleStatusChange = async (taskId, newStatus, options = {}) => {
        const targetTask = milestones.find(t => String(t._id) === String(taskId)) || tasks.find(t => String(t._id) === String(taskId)) || tableTasks.find(t => String(t._id) === String(taskId));
        if (targetTask && targetTask.itemType === 'milestone' && newStatus === 'Completed' && !options.skipModal) {
            setCompletingMilestone(targetTask);
            setIsCompletionModalOpen(true);
            return;
        }

        setUpdatingStatusId(taskId);
        try {
            const extra = options.deliverableChecklist ? { deliverableChecklist: options.deliverableChecklist } : {};
            await updateTaskStatus(taskId, newStatus, options.note || '', options.completionComments || '', extra);
            setMilestones((prev) => prev.map((m) => (m._id === taskId ? {
                ...m,
                status: newStatus,
                ...(newStatus === 'Completed' ? { progress: 100 } : {}),
                ...(options.deliverableChecklist ? { deliverableChecklist: options.deliverableChecklist } : {})
            } : m)));
            setTasks((prev) => prev.map((t) => (t._id === taskId ? {
                ...t,
                status: newStatus,
                ...(options.deliverableChecklist ? { deliverableChecklist: options.deliverableChecklist } : {})
            } : t)));
            setTableTasks((prev) => prev.map((t) => (t._id === taskId ? {
                ...t,
                status: newStatus,
                ...(options.deliverableChecklist ? { deliverableChecklist: options.deliverableChecklist } : {})
            } : t)));
            if (activeNav !== 'milestones') {
                loadTaskSummary();
            }
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to update status.');
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const handleConfirmMilestoneCompletion = async (comments, verifiedChecklist) => {
        if (!completingMilestone) return;
        setCompletionLoading(true);
        try {
            await handleStatusChange(completingMilestone._id, 'Completed', {
                skipModal: true,
                completionComments: comments,
                deliverableChecklist: verifiedChecklist,
            });
            setIsCompletionModalOpen(false);
            setCompletingMilestone(null);
            message.success('Milestone marked as completed and synced to CRE!');
        } catch {
            message.error('Failed to complete milestone.');
        } finally {
            setCompletionLoading(false);
        }
    };

    const resolveInitialProjectTab = (project) => {
        if (!project) return 'list';
        const views = project.availableViews || project.customFields?.availableViews || { list: true, kanban: true, timeline: true, sprint: true };
        const sprintEnabled = (project.sprintManagement ?? project.autoSprintPlanning ?? true);

        const isAvailable = (key) => {
            if (key === 'sprint' && !sprintEnabled) return false;
            if (views && views[key] !== undefined) return Boolean(views[key]);
            return true;
        };

        const requestedView = project.defaultView || project.customFields?.defaultView || 'Kanban Board View';
        const lower = String(requestedView).toLowerCase();
        let requestedKey = 'list';
        if (lower.includes('sprint')) requestedKey = 'sprint';
        else if (lower.includes('kanban')) requestedKey = 'kanban';
        else if (lower.includes('timeline')) requestedKey = 'timeline';

        if (isAvailable(requestedKey)) return requestedKey;

        const fallbackOrder = ['list', 'kanban', 'timeline', 'sprint'];
        const firstValid = fallbackOrder.find((k) => isAvailable(k));
        return firstValid || 'list';
    };

    const handleProjectClick = (project) => {
        const projId = typeof project === 'object' && project !== null ? (project._id || project.id) : project;
        if (!projId) return;

        const fullProj = projects.find((p) => String(p._id) === String(projId)) || (typeof project === 'object' ? project : null);
        setDashboardTargetProjectId(projId);
        handleNavChange('dashboard', {
            activeNav: 'dashboard',
            openProjectModal: true,
            projectId: projId,
            project: fullProj,
        });
    };

    const handleShowAllProjects = () => {
        setActiveProject(null);
        setFilters((prev) => ({ ...prev, projectId: '' }));
    };

    const visibleTasks = useMemo(() => {
        if (!canViewAllTasks) {
            return tasks.filter((t) => !t.isDraft && t.status !== 'Draft');
        }
        return tasks;
    }, [tasks, canViewAllTasks]);

    // Filter milestones out from task boards unless explicitly selected via Work Item Type filter (Section 25)
    const dashboardTasks = useMemo(() => {
        return visibleTasks.filter((t) => {
            if (filters.itemType === 'milestone') return t.itemType === 'milestone';
            if (filters.itemType === 'task') return t.itemType !== 'milestone';
            return t.itemType !== 'milestone';
        });
    }, [visibleTasks, filters.itemType]);

    const isFilesNav = activeNav === 'files';

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <ProjectManagementTourProvider>
            <div className={`bg-[#f8f9fa] dark:bg-[#141414] ${isFilesNav ? 'h-[calc(100vh-56px)] max-h-[calc(100vh-56px)] overflow-hidden' : 'min-h-[calc(100vh-56px)]'}`}>

                {/* ── Left sidebar (fixed) ── */}
                <ProjectSidebar
                    isOpen={sidebarOpen}
                    isHidden={isMobileHidden}
                    onToggle={handleSidebarToggle}
                    activeNav={activeNav}
                    onNavChange={handleNavChange}
                    inboxBadgeCount={inboxBadgeCount}
                    myWorkBadgeCount={myWorkBadgeCount}
                    projects={projects}
                />

                {/* ── Main content — offset by sidebar width so nothing is hidden ── */}
                <main
                    className={`min-w-0 transition-all duration-300 ease-in-out ${isFilesNav
                        ? 'p-3 h-full max-h-full overflow-hidden'
                        : 'px-6 py-8 space-y-6'
                        }`}
                    style={{
                        // isMobileHidden → no sidebar, full width
                        // collapsed      → 56px icon rail
                        // expanded       → 256px full sidebar
                        marginLeft: isMobileHidden ? '0px' : sidebarOpen ? '256px' : '56px',
                    }}
                >

                    {/* ── Category 3: Omni/CRE Integration Status Banner (Admins/PMs/BDs) ── */}
                    <CreSyncWarningBanner />

                    {/* PROJECT DASHBOARD — embedded project list & KPI table */}
                    {visitedNavs.has('dashboard') && (
                        <div className={activeNav === 'dashboard' ? 'block' : 'hidden'}>
                            {queryProjectId && (
                                <ProjectDetailsWorkspace
                                    projectId={queryProjectId}
                                    initialTab={queryProjectTab}
                                    openTaskDrawer={openTaskDrawer}
                                    openCreateDrawer={openCreateDrawer}
                                    employees={employees}
                                    currentAdmin={currentAdmin}
                                    onRegisterRefresh={(fn) => { projectDetailsRefreshRef.current = fn; }}
                                    onBack={() => {
                                        history.push('/project-dashboard');
                                    }}
                                    onViewInPipeline={(proj) => {
                                        history.push('/project-management', { projectId: proj?._id || queryProjectId, activeNav: 'overview' });
                                    }}
                                />
                            )}
                            {!queryProjectId && (<ProjectDashboard
                                hideSidebar={true}
                                targetProjectId={dashboardTargetProjectId}
                                onClearTargetProject={() => setDashboardTargetProjectId(null)}
                            />)}
                        </div>
                    )}

                    {/* OVERVIEW — fully functional project dashboard */}
                    {visitedNavs.has('overview') && (
                        <div className={activeNav === 'overview' ? 'block space-y-6' : 'hidden'}>
                            {/* Section 1: Header — title + tour guide on the left, filters on the right */}
                            <section className="flex flex-col min-[1300px]:flex-row min-[1300px]:items-center justify-between gap-4">
                                {/* Left: Title + Tour Guide + Subtitle */}
                                <div data-tour="workspace-header" className="shrink-0">
                                    <div className="flex items-center gap-3">
                                        <h1 className="text-3xl font-black text-[#003263] dark:text-[#a7c8ff] font-headline tracking-tight">
                                            Task Management
                                        </h1>
                                        <TakeTourButton currentNav={activeNav} currentSubTab={activeTaskTab} />
                                    </div>
                                    <p className="text-[#424750] dark:text-slate-400 font-medium mt-0.5">
                                        Enterprise Resource Planning &amp; Performance Tracking
                                    </p>
                                </div>

                                {/* Right / Row 2: Filters */}
                                <div data-tour="dashboard-filters" className="shrink-0 w-fit max-w-full overflow-x-auto sm:overflow-visible pb-1 sm:pb-0">
                                    <DashboardFilters
                                        filters={filters}
                                        onFilterChange={handleFilterChange}
                                        projects={projects}
                                        employees={employees}
                                        activeProject={activeProject}
                                        onProjectClick={handleProjectClick}
                                        onShowAll={handleShowAllProjects}
                                        canViewAllTasks={canViewAllTasks}
                                    />
                                </div>
                            </section>

                            {/* Section 2: Pipeline Cards */}
                            <PipelineFunnelCards
                                summary={taskSummary}
                                tasks={tasks}
                                activeFilter={statusFilter}
                                onFilterByStatus={handleStatusFilterChange}
                            />

                            {/* Section 3: Workload + AI Intelligence Center */}
                            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <WorkloadChart tasks={tasks} loading={loading} />
                                <AIIntelligenceCenter
                                    projectId={filters.projectId || null}
                                    onTaskClick={(task) => openTaskDrawer(task?._id || task)}
                                    pendingAlertId={pendingAlertId}
                                    onPendingAlertConsumed={() => setPendingAlertId(null)}
                                />
                            </section>

                            {/* Section 4: Task Views */}
                            <section data-tour="workspace-task-board" className="space-y-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div data-tour="task-view-tabs" className="flex-shrink-0">
                                        <TaskViewTabs
                                            activeTab={activeTaskTab}
                                            onTabChange={setActiveTaskTab}
                                            availableViews={activeProject?.availableViews || activeProject?.customFields?.availableViews}
                                            sprintManagement={activeProject?.sprintManagement ?? activeProject?.autoSprintPlanning ?? true}
                                        />
                                    </div>

                                    <div
                                        ref={searchContainerRef}
                                        data-tour="task-search-and-create"
                                        className="relative flex items-center gap-2 flex-wrap"
                                    >
                                        <div className="relative">
                                            <SearchOutlined
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                                style={{ fontSize: 16 }}
                                            />
                                            <input
                                                data-tour="task-search-input"
                                                className="bg-white dark:bg-[#1e293b] border border-slate-200 dark:border-slate-700 pl-9 pr-8 py-2 rounded-xl text-sm focus:ring-2 focus:ring-[#28b5fd] w-44 sm:w-60 shadow-sm text-[#191c1d] dark:text-white outline-none transition-all"
                                                placeholder="Search tasks..."
                                                value={searchQuery}
                                                onFocus={() => {
                                                    if (searchQuery.trim().length > 0) setSearchDropdownOpen(true);
                                                }}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Escape') setSearchDropdownOpen(false);
                                                }}
                                            />
                                            {searchQuery && (
                                                <button
                                                    onClick={() => {
                                                        setSearchQuery('');
                                                        setSearchResults([]);
                                                        setSearchDropdownOpen(false);
                                                    }}
                                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xs p-1"
                                                    title="Clear search"
                                                >
                                                    ✕
                                                </button>
                                            )}

                                            {/* ── Search Results Dropdown (Spotlight / Quick-Find) ── */}
                                            {searchDropdownOpen && searchQuery.trim().length > 0 && (
                                                <div className="absolute top-full mt-2 right-0 sm:right-auto sm:left-0 w-80 sm:w-96 bg-white dark:bg-[#1e293b] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                                                    <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center text-xs font-bold text-[#424750] dark:text-slate-300">
                                                        <span className="flex items-center gap-1.5">
                                                            <SearchOutlined className="text-[#00AAF1]" />
                                                            Search Results
                                                        </span>
                                                        {searchLoading ? (
                                                            <span className="text-[#00AAF1] font-medium flex items-center gap-1">
                                                                <LoadingOutlined /> Searching…
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-400 font-normal">
                                                                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                                                        {searchLoading && searchResults.length === 0 ? (
                                                            <div className="py-8 text-center text-xs text-slate-400">
                                                                <LoadingOutlined className="text-lg text-[#00AAF1] mb-2 block" />
                                                                Searching tasks…
                                                            </div>
                                                        ) : searchResults.length === 0 ? (
                                                            <div className="py-8 text-center text-xs text-slate-400 px-4">
                                                                No tasks found matching <span className="font-semibold text-slate-600 dark:text-slate-300">"{searchQuery}"</span>
                                                            </div>
                                                        ) : (
                                                            searchResults.map((task) => (
                                                                <div
                                                                    key={task._id}
                                                                    onClick={() => {
                                                                        openTaskDrawer(task._id);
                                                                        setSearchDropdownOpen(false);
                                                                    }}
                                                                    className="p-3 hover:bg-[#f0f9ff] dark:hover:bg-slate-800/70 cursor-pointer transition-colors flex flex-col gap-1 group"
                                                                >
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <span className="text-[11px] font-bold font-mono text-[#03488A] dark:text-[#28b5fd] group-hover:underline">
                                                                            {task.taskId || 'Task'}
                                                                        </span>
                                                                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                                                            {task.status || 'Backlog'}
                                                                        </span>
                                                                    </div>
                                                                    <div className="text-xs font-semibold text-[#191c1d] dark:text-white truncate">
                                                                        {task.title}
                                                                    </div>
                                                                    <div className="flex items-center justify-between text-[11px] text-[#737782] dark:text-slate-400 mt-0.5">
                                                                        <span className="truncate max-w-[180px]">
                                                                            {task.project?.name || 'No Project'}
                                                                        </span>
                                                                        <span>
                                                                            {task.assignee?.name || task.assignee?.fullName || 'Unassigned'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                    <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-700 text-[11px] text-center text-slate-400">
                                                        Click any task to view or edit in drawer
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {hasTaskCreateAccess && (
                                            <button
                                                data-tour="new-task-btn"
                                                onClick={() => openCreateDrawer()}
                                                className="text-white flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm shadow-sm hover:opacity-90 active:scale-95 transition-all cursor-pointer whitespace-nowrap"
                                                style={{ background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }}
                                            >
                                                <PlusOutlined />
                                                New Task
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {visitedTabs.has('list') && (
                                    <div data-tour="task-list-table" className={activeTaskTab === 'list' ? 'block' : 'hidden'}>
                                        <TaskListTable
                                            tasks={tableTasks}
                                            loading={tableLoading}
                                            pagination={tablePagination}
                                            onEdit={handleEdit}
                                            onDelete={handleDelete}
                                            onStatusChange={handleStatusChange}
                                            onPageChange={handlePageChange}
                                            onRowClick={handleRowClick}
                                            onProjectClick={handleProjectClick}
                                            onConvertType={handleConvertType}
                                            onAddSubtask={(task) => openCreateDrawer('Backlog')}
                                            updatingStatusId={updatingStatusId}
                                            deletingId={deletingId}
                                            searchQuery=""
                                            onClearSearch={() => setSearchQuery('')}
                                            hasActiveFilters={Boolean(statusFilter || filters.owner || filters.assignee || filters.projectId || (filters.dateRange && filters.dateRange !== 'All Time'))}
                                            onResetFilters={() => {
                                                setStatusFilter(null);
                                                setFilters({ dateRange: 'All Time', owner: '', assignee: '', projectId: '', status: '' });
                                                setSearchQuery('');
                                            }}
                                            projects={projects}
                                        />
                                    </div>
                                )}

                                {visitedTabs.has('kanban') && (
                                    <div data-tour="kanban-board" className={activeTaskTab === 'kanban' ? 'block' : 'hidden'}>
                                        <KanbanBoard
                                            tasks={dashboardTasks}
                                            loading={loading}
                                            onStatusChange={handleStatusChange}
                                            onRowClick={handleRowClick}
                                            onEdit={handleEdit}
                                            onDelete={handleDelete}
                                            onAddTask={(status) => openCreateDrawer(status)}
                                        />
                                    </div>
                                )}

                                {visitedTabs.has('timeline') && (
                                    <div data-tour="timeline-board" className={activeTaskTab === 'timeline' ? 'block' : 'hidden'}>
                                        <TimelineBoard
                                            tasks={dashboardTasks}
                                            loading={loading}
                                            onRowClick={handleRowClick}
                                        />
                                    </div>
                                )}

                                {visitedTabs.has('sprint') && (
                                    <div className={activeTaskTab === 'sprint' ? 'block' : 'hidden'}>
                                        <SprintBoard
                                            tasks={dashboardTasks}
                                            loading={loading}
                                            onStatusChange={handleStatusChange}
                                            onRowClick={handleRowClick}
                                        />
                                    </div>
                                )}
                            </section>
                        </div>
                    )}

                    {/* MILESTONES — dedicated Milestones Page */}
                    {visitedNavs.has('milestones') && (
                        <section className={`space-y-6 ${activeNav === 'milestones' ? 'block' : 'hidden'}`}>
                            {/* Header — title + tour guide on the left, filters on the right */}
                            <div data-tour="milestone-header" className="flex flex-col min-[1300px]:flex-row min-[1300px]:items-center justify-between gap-4">
                                {/* Left: Title + Tour Guide + Subtitle */}
                                <div className="shrink-0">
                                    <div className="flex items-center gap-3">
                                        <h1 className="text-3xl font-black text-[#003263] dark:text-[#a7c8ff] font-headline tracking-tight">
                                            Project Milestones
                                        </h1>
                                        <TakeTourButton currentNav="milestones" onNavigate={handleNavChange} />
                                    </div>
                                    <p className="text-[#424750] dark:text-slate-400 font-medium mt-0.5">
                                        Track major project stages, deliverables, approvals, and connected phase tasks.
                                    </p>
                                </div>

                                {/* Right / Row 2: Filters */}
                                <div className="shrink-0 w-fit max-w-full overflow-x-auto sm:overflow-visible pb-1 sm:pb-0">
                                    <DashboardFilters
                                        filters={filters}
                                        onFilterChange={handleFilterChange}
                                        projects={projects}
                                        employees={employees}
                                        activeProject={activeProject}
                                        onProjectClick={handleProjectClick}
                                        onShowAll={handleShowAllProjects}
                                        canViewAllTasks={canViewAllTasks}
                                    />
                                </div>
                            </div>

                            <MilestoneCardView
                                milestones={milestones}
                                loading={milestonesLoading}
                                onMilestoneClick={(ms) => openTaskDrawer(ms._id)}
                                onStatusChange={handleStatusChange}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onConvertType={handleConvertType}
                                onCreateMilestone={(canCreateMilestone || Boolean(activeProject && (String(activeProject.projectManager?._id || activeProject.projectManager || '') === String(currentAdmin?._id) || String(activeProject.createdBy?._id || activeProject.createdBy || '') === String(currentAdmin?._id)))) ? () => openCreateDrawer('Backlog', 'milestone') : null}
                            />
                        </section>
                    )}

                    {/* PROJECT FILES — Centralized Production File & Project Asset Management */}
                    {visitedNavs.has('files') && (
                        <div className={activeNav === 'files' ? 'block' : 'hidden'}>
                            <ProjectFiles onNavigate={handleNavChange} />
                        </div>
                    )}

                    {/* INBOX — Centralized Action & Activity Control Center */}
                    {visitedNavs.has('inbox') && (
                        <div className={activeNav === 'inbox' ? 'block' : 'hidden'}>
                            <ProjectInbox
                                workspaceTasks={tasks}
                                onOpenTaskDrawer={openTaskDrawer}
                                onNavigate={handleInboxNavigate}
                                onSelectProject={setActiveProject}
                            />
                        </div>
                    )}

                    {/* MY WORK — Personalized Employee Workspace */}
                    {visitedNavs.has('mywork') && (
                        <div className={activeNav === 'mywork' ? 'block' : 'hidden'}>
                            <MyWork
                                allTasks={tasks}
                                loading={loading}
                                projects={projects}
                                employees={employees}
                                openTaskDrawer={openTaskDrawer}
                                openCreateDrawer={openCreateDrawer}
                                onStatusChange={handleStatusChange}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onConvertType={handleConvertType}
                                onTaskCreated={handleTaskCreated}
                                onNavigate={handleInboxNavigate}
                            />
                        </div>
                    )}


                    {/* REPORTS & ANALYTICS — Single-Page Operational & Forecasting Workspace */}
                    {visitedNavs.has('reports') && (
                        <div className={activeNav === 'reports' ? 'block' : 'hidden'}>
                            <ReportsWorkspace
                                employees={employees}
                                projects={projects}
                                onSelectTask={(taskId) => openTaskDrawer(taskId)}
                                onSelectProject={(projId) => {
                                    history.push(`/project-management?projectId=${projId}&tab=overview`);
                                }}
                            />
                        </div>
                    )}


                    {activeNav !== 'overview' && activeNav !== 'milestones' && activeNav !== 'dashboard' && activeNav !== 'files' && activeNav !== 'inbox' && activeNav !== 'mywork' && activeNav !== 'reports' && (
                        <div className="flex items-center justify-center min-h-[60vh]">
                            <div className="text-center space-y-3">
                                <ToolOutlined style={{ fontSize: 48, color: '#c2c6d2' }} />
                                <p className="text-base font-semibold text-[#424750] dark:text-slate-400">
                                    {activeNav.charAt(0).toUpperCase() + activeNav.slice(1)} coming in a future phase.
                                </p>
                            </div>
                        </div>
                    )}
                </main>

                {/* ── Modals ── */}
                <React.Suspense fallback={null}>
                    {isCreateProjectOpen && (
                        <CreateProjectModal
                            open={isCreateProjectOpen}
                            projectToEdit={projectToEdit}
                            onClose={() => { setIsCreateProjectOpen(false); setProjectToEdit(null); }}
                            onSuccess={handleProjectCreated}
                        />
                    )}
                </React.Suspense>

                <TaskDetailDrawer
                    mode={drawerMode}
                    taskId={drawerTaskId}
                    itemType={drawerItemType}
                    initialMilestoneId={drawerInitialMilestoneId}
                    open={isDrawerOpen}
                    onClose={closeTaskDrawer}
                    onSuccess={handleTaskCreatedAndOpen}
                    selectedProject={drawerSelectedProject}
                    selectedAssignee={filters.assignee}
                    defaultStatus={drawerDefaultStatus}
                    currentAdminId={currentAdmin?._id}
                    currentAdmin={currentAdmin}
                    onTaskUpdated={handleTaskUpdated}
                    employees={employees}
                    projects={projects}
                    workspaceTasks={tasks}
                />

                <MilestoneCompletionModal
                    open={isCompletionModalOpen}
                    onClose={() => { setIsCompletionModalOpen(false); setCompletingMilestone(null); }}
                    onConfirm={handleConfirmMilestoneCompletion}
                    loading={completionLoading}
                    milestone={completingMilestone || {}}
                />

                <MilestoneTourSynchronizer
                    tasks={tasks}
                    openTaskDrawer={openTaskDrawer}
                    isDrawerOpen={isDrawerOpen}
                    closeTaskDrawer={closeTaskDrawer}
                />

                <ProjectManagementTour />
            </div>
        </ProjectManagementTourProvider>
    );
}
